<template>
  <div class="flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--title slide-down-enter-active">
        {{ user ? user.userName : 'User Details' }}
      </span>
      <a-button size="large" class="a-header--button slide-down-enter-active">
        <router-link :to="'/user-management/list'">
          <a-icon type="arrow-left" />
          Back to users
        </router-link>
      </a-button>
    </div>
    <div v-if="isLoading" class="d-flex justify-content-center align-items-center pt-35-px">
      <Spinner />
    </div>
    <div v-else-if="!user" class="a-body p-20-px">
      <a-alert
        type="error"
        show-icon
        message="The user could not be loaded."
      >
        <template slot="description">
          <p v-if="loadError" class="mb-2">
            {{ loadError }}
          </p>
          <a-button type="primary" size="small" @click="loadUser">
            Try again
          </a-button>
        </template>
      </a-alert>
    </div>
    <template v-else>
      <div class="user-meta">
        <span>Customer ID: {{ user.customerId }}</span>
        <a-tag class="user-status text-capitalize" :color="statusColor">
          {{ user.status }}
        </a-tag>
        <span>Last login: {{ lastLoginText }}</span>
      </div>
      <a-tabs :activeKey="$route.name" :animated="false" class="user-tabs" @change="onTabChange">
        <a-tab-pane v-for="tab in TABS" :key="tab.name" :tab="tab.label" />
      </a-tabs>
      <router-view :user="user" @saved="onSaved" />
    </template>
  </div>
</template>

<script>
  import moment from 'moment';
  import { isNavigationFailure } from 'vue-router';
  import UserManagementService from '../services/UserManagement.service';

  const TABS = [
    { name: 'user-management-account', label: 'Account' },
    { name: 'user-management-access', label: 'Access' },
    { name: 'user-management-rbac', label: 'RBAC' }
  ];

  const STATUS_COLORS = {
    verified: 'green',
    disabled: 'red',
    suspended: 'orange'
  };

  export default {
    name: 'UserDetail',
    data() {
      return {
        TABS,
        user: null,
        isLoading: true,
        loadError: '',
        userRequest: 0
      };
    },
    computed: {
      userId() {
        return this.$route.params.id;
      },
      statusColor() {
        return STATUS_COLORS[this.user.status];
      },
      // The API sends last_login as UTC without a zone, so it must not be parsed as local time.
      lastLoginText() {
        return this.user.lastLogin ? `${moment.utc(this.user.lastLogin).format('YYYY-MM-DD HH:mm:ss')} (UTC)` : 'Never';
      }
    },
    watch: {
      // History can jump between two detail pages (e.g. /1/account to /2/account), which reuses this instance.
      userId: 'loadUser'
    },
    created() {
      this.loadUser();
    },
    methods: {
      async loadUser() {
        this.isLoading = true;
        this.user = null;
        this.loadError = '';
        const request = ++this.userRequest;
        try {
          const user = await UserManagementService.detailUserById(this.userId);
          if (request !== this.userRequest) {
            return;
          }
          this.user = user;
        } catch (error) {
          if (request !== this.userRequest) {
            return;
          }
          this.loadError = (error.messages || []).join(' ');
        } finally {
          if (request === this.userRequest) {
            this.isLoading = false;
          }
        }
      },
      async onSaved() {
        const request = ++this.userRequest;
        try {
          const user = await UserManagementService.detailUserById(this.userId);
          if (request !== this.userRequest) {
            return;
          }
          this.user = user;
        } catch (error) {
          if (request !== this.userRequest) {
            return;
          }
          this.$showMultiErrorMessage(error.messages);
        }
      },
      onTabChange(name) {
        // A tab with unsaved changes can cancel the navigation; that is not an error.
        this.$router.push({ name, params: { id: this.userId } }).catch((error) => {
          if (!isNavigationFailure(error)) {
            throw error;
          }
        });
      }
    }
  };
</script>

<style lang="scss" scoped>
  .user-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 24px;
    padding: 12px 20px;
    flex-shrink: 0;
  }
  .user-status {
    margin: 0;
  }
  .user-tabs {
    padding: 0 20px;
    flex-shrink: 0;
    ::v-deep .ant-tabs-bar {
      margin-bottom: 0;
    }
  }
</style>
